import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Snackbar, Alert, Button } from '@mui/material';
import { useNav } from './App';
import { C } from './theme';
import api from './services/api';

// ─── Inline SVG icons (Lucide, 1.8px stroke, rounded caps) ───────────────────
// Kept inline to avoid adding an npm dependency mid-session.
// All sized at 22×22, viewBox 0 0 24 24.

function IconUsers({ color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

function IconZap({ color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
}

function IconHome({ color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <path d="M9 21v-8h6v8"/>
    </svg>
  );
}

function IconMessageCircle({ color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6C8.686 6 6 8.686 6 12c0 1.29.387 2.49 1.05 3.49L6 21l5.51-1.05A6 6 0 1 0 12 6z"/>
    </svg>
  );
}

function IconUser({ color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

// ─── Tab definitions ──────────────────────────────────────────────────────────
// icon field is now a render function: (color) => <SVG />

const TABS = [
  { id: 'inhere',  label: 'In Here', icon: (c) => <IconUsers color={c} />,          path: '/inhere'  },
  { id: 'play',    label: 'Play',    icon: (c) => <IconZap color={c} />,             path: '/game'    },
  { id: 'home',    label: 'Home',    icon: (c) => <IconHome color={c} />,            path: '/home'    },
  { id: 'chats',   label: 'Chats',   icon: (c) => <IconMessageCircle color={c} />,   path: '/chats'   },
  { id: 'profile', label: 'My Card', icon: (c) => <IconUser color={c} />,            path: '/profile' },
];

function getActiveTab(path) {
  if (path === '/inhere')           return 'inhere';
  if (path === '/game')             return 'play';
  if (path === '/home')             return 'home';
  if (path === '/chats')            return 'chats';
  if (path === '/profile')          return 'profile';
  if (path.startsWith('/profile/')) return 'profile';
  if (path.startsWith('/chat/'))    return 'chats';
  if (path === '/game')             return 'play';
  return 'home';
}

export default function AppShell({ path, children }) {
  const navigate = useNav();
  const activeTab = getActiveTab(path);
  const scrollRef = useRef(null);

  const [badges, setBadges]             = useState({ inhere: 0, play: false, chats: 0 });
  const [checkinState, setCheckinState] = useState(null); // null=loading, false=not checked in, object=checkin
  const [sessionExpired, setSessionExpired] = useState(false);
  const [gateMessage, setGateMessage]   = useState('');

  const fetchCheckinState = useCallback(async () => {
    try {
      const r = await api.get('/checkin/my-checkin');
      if (r.data.checkedIn && r.data.checkin) {
        const expiry = new Date(r.data.checkin.expires_at).getTime();
        if (Date.now() > expiry) {
          setCheckinState(false);
          setSessionExpired(true);
        } else {
          setCheckinState(r.data.checkin);
          setSessionExpired(false);
        }
      } else {
        setCheckinState(false);
      }
    } catch (_) {
      setCheckinState(false);
    }
  }, []);

  // Backoff+jitter state for the badge poll only. Plain refs (not state)
  // since they don't need to trigger re-renders.
  const badgeFailuresRef = useRef(0);
  const badgeTimerRef    = useRef(null);

  const BADGE_BASE_INTERVAL_MS = 10000;   // normal cadence, unchanged
  const BADGE_MAX_DELAY_MS     = 60000;   // cap the backoff window

  const fetchBadges = useCallback(async () => {
    try {
      const r = await api.get('/notifications/counts');
      setBadges({
        inhere: r.data.new_arrivals  || 0,
        play:   r.data.game_starting || false,
        chats:  r.data.unread_chats  || 0,
      });
      badgeFailuresRef.current = 0; // reset backoff on success
    } catch (_) {
      badgeFailuresRef.current += 1;
    }
  }, []);

  // Self-scheduling badge poll: on success, next call is base interval away.
  // On failure, delay grows exponentially (capped) and is randomized so
  // clients that failed at the same instant don't retry at the same instant.
  useEffect(() => {
    let cancelled = false;

    async function scheduleNext() {
      if (cancelled) return;
      await fetchBadges();
      if (cancelled) return;

      const failures = badgeFailuresRef.current;
      const delay = failures === 0
        ? BADGE_BASE_INTERVAL_MS
        : Math.random() * Math.min(BADGE_BASE_INTERVAL_MS * 2 ** failures, BADGE_MAX_DELAY_MS);

      badgeTimerRef.current = setTimeout(scheduleNext, delay);
    }

    scheduleNext();

    return () => {
      cancelled = true;
      if (badgeTimerRef.current) clearTimeout(badgeTimerRef.current);
    };
  }, [fetchBadges]);

  // Checkin state stays on its original fixed interval — unaffected by BD6.
  useEffect(() => {
    fetchCheckinState();
    const interval = setInterval(fetchCheckinState, 10000);
    return () => clearInterval(interval);
  }, [fetchCheckinState]);

  function handleTabPress(tab) {
    if ((tab.id === 'inhere' || tab.id === 'play') && checkinState === false) {
      setGateMessage('Check into a venue first to use this tab.');
      return;
    }
    if (tab.id === 'inhere') setBadges(b => ({ ...b, inhere: 0 }));
    if (tab.id === 'play')   setBadges(b => ({ ...b, play: false }));
    if (tab.id === 'chats')  setBadges(b => ({ ...b, chats: 0 }));
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    navigate(tab.path);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: C.bgApp }}>

      {/* Session expired banner */}
      {sessionExpired && (
        <Box sx={{
          background: C.bgElevated,
          borderBottom: `1px solid ${C.border}`,
          padding: '10px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography sx={{ fontSize: '13px', color: C.orange }}>
            Your session has ended
          </Typography>
          <Button size="small" sx={{ fontSize: '12px', color: C.orange }}
            onClick={() => { setSessionExpired(false); navigate('/home'); }}>
            Check in again →
          </Button>
        </Box>
      )}

      {/* Page content */}
      <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto' }}>
        {children}
      </Box>

      {/* Bottom nav */}
      <Box sx={{
        height: '64px',
        background: C.bgElevated,
        borderTop: `0.5px solid ${C.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '0 8px',
        flexShrink: 0,
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
      }}>
        {TABS.map(tab => {
          const isActive   = activeTab === tab.id;
          const iconColor  = isActive ? C.yellow : C.textDisabled;
          const labelColor = isActive ? C.yellow : C.textDisabled;
          const badgeCount = tab.id === 'inhere' ? badges.inhere
                           : tab.id === 'chats'  ? badges.chats : 0;
          const hasDot     = tab.id === 'play' && badges.play;

          return (
            <Box key={tab.id}
              onClick={() => handleTabPress(tab)}
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '3px', cursor: 'pointer', padding: '6px 12px',
                borderRadius: '12px', position: 'relative', minWidth: '56px',
                transition: 'background 0.15s',
                '&:hover': { background: 'rgba(255,255,255,0.05)' },
              }}
            >
              {/* SVG icon */}
              {tab.icon(iconColor)}

              {/* Label */}
              <Typography sx={{
                fontSize: '9px',
                fontWeight: 500,
                letterSpacing: '0.2px',
                color: labelColor,
                transition: 'color 0.15s',
              }}>
                {tab.label}
              </Typography>

              {/* Active indicator dot */}
              {isActive && (
                <Box sx={{
                  position: 'absolute', bottom: 0, left: '50%',
                  transform: 'translateX(-50%)',
                  width: '4px', height: '4px', borderRadius: '50%',
                  background: C.yellow,
                }} />
              )}

              {/* Count badge (inhere / chats) */}
              {badgeCount > 0 && (
                <Box sx={{
                  position: 'absolute', top: '2px', right: '4px',
                  background: C.orange, color: C.bgApp,
                  fontSize: '9px', fontWeight: 600,
                  minWidth: '16px', height: '16px', borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 4px', border: `1.5px solid ${C.bgApp}`,
                }}>
                  {badgeCount > 9 ? '9+' : badgeCount}
                </Box>
              )}

              {/* Dot badge for Play (game starting) */}
              {hasDot && (
                <Box sx={{
                  position: 'absolute', top: '4px', right: '10px',
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: C.orange, border: `1.5px solid ${C.bgApp}`,
                }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Gate message snackbar */}
      <Snackbar
        open={!!gateMessage}
        autoHideDuration={3000}
        onClose={() => setGateMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: '80px' }}
      >
        <Alert severity="info" onClose={() => setGateMessage('')}
          sx={{ background: C.bgElevated, color: C.textPrimary, border: `1px solid ${C.border}` }}>
          {gateMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
}
